import { useEffect } from 'react';
import { useAuthStore } from '@/store/auth';
import { pushRecent } from '@/lib/recents';

/**
 * Records the current page in the recents localStorage layer.
 *
 * Call once per top-level page component, near the top of the
 * component body, with a human-readable label:
 *
 *   useRecentRoute('Dashboard');
 *   useRecentRoute('Locations');
 *
 * For detail pages where the label depends on resolved data,
 * pass the resolved label and let the dependency array re-fire
 * when it lands:
 *
 *   const { data: location } = useQuery(...);
 *   useRecentRoute(location ? `${location.name} (Location)` : '');
 *
 * An empty-string label is a no-op — the hook waits for a
 * non-empty label before pushing. This is the recommended
 * pattern for detail pages: pass '' until data resolves, then
 * pass the meaningful label. Avoids polluting recents with
 * "Loading…" or generic placeholders.
 *
 * Reactivity
 * ──────────
 * Pathname is snapshotted from window.location.pathname at
 * effect-fire time. The admin app re-mounts page components on
 * route change (App.tsx returns a different component per path),
 * so the hook fires fresh on each navigation. Including pathname
 * in the dep array is forward-safe — if the routing pattern
 * later changes to keep components mounted across path changes,
 * the hook will still re-fire correctly.
 *
 * Tenant scoping
 * ──────────────
 * Reads tenant_id from the auth store. No-op if tenant_id is
 * missing (unauthenticated state should not reach a page using
 * this hook in practice; the no-op is a defensive belt).
 */
export function useRecentRoute(label: string): void {
  const tenantId = useAuthStore((s) => s.user?.tenant_id ?? null);

  useEffect(() => {
    if (!tenantId) return;
    if (!label) return;
    if (typeof window === 'undefined') return;
    const href = window.location.pathname;
    if (!href) return;
    pushRecent(tenantId, {
      href,
      label,
      visited_at: Date.now(),
    });
  }, [tenantId, label]);
}
